import * as React from "react"
import { useRecoilValue, useRecoilState } from "recoil"
import { VideoPlayer } from "../../videoPlayer"
import { useBrowserType } from "../../../../hooks/useBrowserType"
import { useWatchParty } from "../../../../hooks/useWatchParty"
import useWindowSize from "../../../../hooks/useWindowSize"
import { MobileHeader } from "../mobile-header"
import { findSideRatio } from "../../../../util/findSideRatio"
import { playerPropsSelector } from "../../../../recoil/atoms/player"
import {
  productOverlaySelector
} from "../../../../recoil/atoms/product-overlay"
import { LocalUserProperties } from "../../../../types/localUserProps"
import { Flex } from "@chakra-ui/react"
import { isBigPlayIconState } from "../../../../recoil/atoms/bigPlayIcon"
import { watchPartyHideControlsSelector } from "../../../../recoil/atoms/watchPartyScreen"
import { useLocalUserType } from "../../../../hooks/useLocalUserType"
import { viewDependingLayoutAtom } from "../../../../recoil/atoms/view"
import layoutStates from "../../../../recoil/atoms/layout"

interface MobileVideoScreenProps {
  isLandscape: boolean
  isOnlyScreensView: boolean
  videoSize: {
    width: number
    height: number
  }
  localUserProperties: LocalUserProperties
  showLeaveOverlay: boolean
  setShowLeaveOverlay: any
  isUrlBarNarrowed: boolean
  changeSizeIconDependingHeightVideo?: (
    minHeightIcon: number,
    maxHeightIcon: number
  ) => number
}

export const MobileVideoScreen: React.FC<MobileVideoScreenProps> = ({
  isLandscape,
  isOnlyScreensView,
  videoSize,
  localUserProperties,
  showLeaveOverlay,
  setShowLeaveOverlay,
  isUrlBarNarrowed,
  changeSizeIconDependingHeightVideo
}) => {
  const { screenSize } = useWindowSize()
  const { isChromeIOS, isAndroidMobile, isSamsungBrowser } = useBrowserType()
  const { isOpen } = useRecoilValue(productOverlaySelector)
  const { isTransparent } = useRecoilValue(viewDependingLayoutAtom)
  const { isOpenControlsMobile } = useRecoilValue(playerPropsSelector)

  const defaultHeightMainScreen = React.useMemo<number>(() => {
    return findSideRatio(screenSize.width, 9, 16)
  }, [screenSize.width])

  const defaultWidthMainScreen = React.useMemo<number>(() => {
    return findSideRatio(screenSize.height, 16, 9)
  }, [screenSize.height])

  const { isWatchParty, watchPartyHostId } = useWatchParty()
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)

  const _stylingWatchParty: React.CSSProperties = React.useMemo(() => {
    if (isWatchParty) {
      return {
        width: isFloatingDivActive
          ? "100%"
          : isLandscape
          ? defaultWidthMainScreen + "px"
          : videoSize.width + "px",
        maxWidth: screenSize.width + "px",
        // height: isFloatingDivActive
        //   ? "100%"
        //   : isOnlyScreensView
        //   ? `${defaultHeightMainScreen}px`
        //   : isLandscape
        //   ? screenSize.height + "px"
        //   : videoSize.height + "px"
        height: "100%"
      }
    } else {
      return {
        width: "100%",
        height: "100%"
      }
    }
  }, [
    isFloatingDivActive,
    defaultHeightMainScreen,
    defaultWidthMainScreen,
    isLandscape,
    isOnlyScreensView,
    isWatchParty,
    screenSize.height,
    screenSize.width,
    videoSize.height,
    videoSize.width
  ])

  const hideControls = useRecoilValue(watchPartyHideControlsSelector)
  const { isModerator } = useLocalUserType()
  const [, setBigPlayIcon] = useRecoilState<boolean>(isBigPlayIconState)
  const heightLeaveOverlay = React.useMemo<string>(
    () =>
      !(!isModerator && hideControls) &&
      watchPartyHostId !== "modNotPresentImage" &&
      watchPartyHostId !== "modNotPresentOverlay" &&
      isOpenControlsMobile &&
      !isOpen
        ? "calc(100% - 55px)"
        : "100%",
    [hideControls, isModerator, isOpenControlsMobile, isOpen, watchPartyHostId]
  )

  const videoPlayer = document.getElementById(
    "watchpartyvideoplayer"
  ) as HTMLVideoElement
  //TODO
  const handleEnterMobilePictureInPicture = () => {
    setBigPlayIcon(false)
  }

  React.useEffect(() => {
    if (videoPlayer) {
      videoPlayer.addEventListener(
        "enterpictureinpicture",
        handleEnterMobilePictureInPicture
      )

      return () => {
        videoPlayer.removeEventListener(
          "enterpictureinpicture",
          handleEnterMobilePictureInPicture
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlayer])

  return (
    <Flex>
      <VideoPlayer
        isMobile={true}
        showLeave={true}
        isTransparent={isTransparent}
        widthMainScreen={
          isLandscape
            ? findSideRatio(screenSize.height, 16, 9)
            : videoSize.width
        }
        maxHeightMainScreen={
          isLandscape ? screenSize.height : defaultHeightMainScreen
        }
        maxWidthOverlay={
          isLandscape
            ? screenSize.width < findSideRatio(screenSize.height, 16, 9)
              ? screenSize.width - 60
              : findSideRatio(screenSize.height, 16, 9) * 0.7
            : videoSize.width - 60
        }
        isLandscape={isLandscape}
        header={<MobileHeader isLandscape={isLandscape} isVideoHeader={true} />}
        heightLeaveOverlay={heightLeaveOverlay}
        localUserProperties={localUserProperties}
        setShowLeaveOverlay={setShowLeaveOverlay}
        showLeaveOverlay={showLeaveOverlay}
        hideSpinner={
          watchPartyHostId === "modNotPresentImage" ||
          watchPartyHostId === "modNotPresentOverlay"
        }
        widthLeaveOverlay={
          !isSamsungBrowser &&
          !isAndroidMobile &&
          !isChromeIOS &&
          isLandscape &&
          defaultWidthMainScreen + "px"
        }
        styling={_stylingWatchParty}
        isMainMobileScreen={true}
        isUrlBarNarrowed={isUrlBarNarrowed}
        changeSizeIconDependingHeightVideo={changeSizeIconDependingHeightVideo}
      />
    </Flex>
  )
}
